/* eslint-disable camelcase */
import { Button, Grid, Stack, TextField, Tooltip, Typography } from '@mui/material'
import AgriAction from 'agriAdministration/actions/AgriAction'
import { push } from '@lagunovsky/redux-react-router'
import { compact, groupBy, template, uniqBy } from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { getSetting, getSettingInt } from 'utils/SettingUtils'
import { formatMilliers } from 'utils/StringUtil'
import ModalNothingDeclared from '../modals/ModalNothingDeclared'
import { CHRONICLES_CONSTANTS, POINT_STATUS_DECLARATION, SITU_POINT_PUMP, SITU_PUMP_COUNTER, SURVEY_TYPES } from 'agriAdministration/constants/AgriConstants'
import moment from 'moment'
import { hasValue } from 'utils/NumberUtil'
import ProgressCard from 'components/card/ProgressCard'
import { useParams } from 'react-router'
import { People } from '@mui/icons-material'
import InstallationAction from 'installation/actions/InstallationAction'

const cellStyle = { borderLeft: '1px solid black', borderBottom: '1px solid black' }

const DeclarationRecapStep = ({
    readMode,
    comment,
    setComment = () => {},
}) => {
    const [dataLoaded, setDataLoaded] = useState(false)
    const [openModalNothingDeclared, setOpenModalNothingDeclared] = useState(false)

    const {
        survey,
        variousMateriels,
        exploitation,
        surveyVolumesExploitation,
        installationsWithGeo,
        contributorsIndex,
        exploitationsExportFullData,
        declaration,
    } = useSelector(store => ({
        survey: store.AgriReducer.survey,
        variousMateriels: store.VariousMaterielReducer.variousMateriels,
        exploitation: store.AgriReducer.exploitation,
        surveyVolumesExploitation: store.AgriReducer.surveyVolumesExploitation,
        installationsWithGeo: store.InstallationReducer.installationsWithGeo,
        contributorsIndex: store.ContributorReducer.contributorsIndex,
        exploitationsExportFullData: store.AgriReducer.exploitationsExportFullData,
        declaration: store.AgriReducer.declaration,
    }), shallowEqual)

    const dispatch = useDispatch()
    const params = useParams()

    const volumesPoints = useMemo(() => declaration.link_declarationInstallation.map((point) => {
        const volumesAsked = point.link_usages.reduce((acc, usage) => acc + (isNaN(parseInt(usage.requestedYearVolume)) ? 0 : usage.requestedYearVolume), 0)
        const lowWaterVolumeAsked = point.link_usages.reduce((acc, usage) => acc + (isNaN(parseInt(usage.lowWaterVolume)) ? 0 : usage.lowWaterVolume), 0)
        return { id: point.idInstallation, volumesAsked, lowWaterVolumeAsked, nbUsages: point.link_usages.length }
    }), [declaration])

    useEffect(() => {
        dispatch(AgriAction.calculSurveyExploitationVolumes(survey.idSurvey, declaration.idExploitation)).then(() => {
            if (!installationsWithGeo.length) {
                dispatch(InstallationAction.fetchInstallationsWithGeo()).then(() => {
                    const usedPoints = declaration.link_declarationInstallation.filter((p) => p.stateCode === POINT_STATUS_DECLARATION.USED)
                    if (usedPoints.length === 0 && volumesPoints.map(({ volumesAsked }) => volumesAsked) === 0 && getSettingInt(survey.link_params, 'step3') === 1) {
                        setOpenModalNothingDeclared(true)
                    }
                    setDataLoaded(true)
                })
            } else {
                const usedPoints = declaration.link_declarationInstallation.filter((p) => p.stateCode === POINT_STATUS_DECLARATION.USED)
                if (usedPoints.length === 0 && volumesPoints.map(({ volumesAsked }) => volumesAsked) === 0 && getSettingInt(survey.link_params, 'step3') === 1) {
                    setOpenModalNothingDeclared(true)
                }
                setDataLoaded(true)
            }
        })
    }, [])

    const getMateriels = (idInstallation) => {
        const link_equipments = declaration.link_declarationInstallation.find((p) => p.idInstallation === idInstallation).link_equipments || []
        const newMats = link_equipments.filter((mLink) => !mLink.idMatAttachment && mLink.mode !== 'd')
        const deletedMats = link_equipments.filter((mLink) => !mLink.idMatAttachment && mLink.mode === 'd')
        const deletedCounters = link_equipments.filter((mLink) => mLink.idMatAttachment && mLink.mode === 'd')
        const exploitationPumps = declaration.link_exploitationMateriel.filter((m) => m.siteType === SITU_POINT_PUMP &&
            m.siteCode === idInstallation &&
            !deletedMats.find((mLink) => m.idVarious === mLink.idMat),
        ) || []
        const mergedPumpsIds = uniqBy([
            ...exploitationPumps.map((m) => m.idVarious),
            ...newMats.map((m) => m.idMat),
        ], obj => obj)
        const exploitationCountersId = compact(mergedPumpsIds.flatMap((pumpId) => {
            const pumpSitu = declaration.link_exploitationMateriel.filter((situ) => situ.siteType === SITU_PUMP_COUNTER &&
                situ.siteCode === pumpId &&
                !deletedCounters.find((mLink) => pumpId === mLink.idMat),
            )
            if (pumpSitu) {
                return pumpSitu.map((s) => s.idVarious)
            }
            return null
        }))
        const newCounters = link_equipments.filter((mLink) => mLink.idMatAttachment && mLink.mode !== 'd' && mergedPumpsIds.includes(mLink.idMat))
        const mergedCountersIds = uniqBy([
            ...exploitationCountersId,
            ...newCounters.map((m) => m.idMatAttachment),
        ], obj => obj)
        const materiels = variousMateriels.filter((m) => [...mergedPumpsIds, ...mergedCountersIds].includes(m.id) &&
            (!hasValue(m.administrator) || (m.administrator === exploitation.operatorCode) || m.isShared),
        )
        return materiels
    }

    const getStatusLabel = (code) => {
        switch (code) {
            case POINT_STATUS_DECLARATION.USED:
                return i18n.used
            case POINT_STATUS_DECLARATION.REMOVED:
                return i18n.shut
            case POINT_STATUS_DECLARATION.ADJOURNED:
                return i18n.adjourned
            default:
                return i18n.unknown
        }
    }

    const getEstimConso = (materielsIds, linkPoint) => {
        const yearReal = survey.surveyType === SURVEY_TYPES.INTERMEDIATE ? survey.year : survey.year - 1
        const chroniclesMat = exploitation.link_chronicles.filter((c) => c.measureType === CHRONICLES_CONSTANTS.TYPE_ESTIM
            && materielsIds.includes(c.idMat)
            && moment(c.measureDate).year() === yearReal
            && (!linkPoint.startDate || c.measureDate >= linkPoint.startDate)
            && (!linkPoint.endDate || c.measureDate < linkPoint.endDate))
        const groupedByMat = Object.values(groupBy(chroniclesMat, 'idMat'))
        return groupedByMat.map((chroniques) => {
            return chroniques.reduce((acc, c) => acc + c.value, 0) || 0
        }).reduce((acc, c) => acc + c, 0)
    }

    const getLineRepartition = (link) => {
        const exploitationFound = exploitationsExportFullData.find((e) => e.idExploitation === link.idExploitation) || {}
        return (
            <>
                <span>{exploitationFound.codification} : {hasValue(link.distribution) ? `${link.distribution} %` : i18n.unknown}</span><br />
            </>
        )
    }

    const date = survey.endDate ? new Date(survey.endDate).toLocaleString() : ''

    if (!dataLoaded) {
        return (
            <Stack padding={1}>
                <ProgressCard withMessage indeterminate round />
            </Stack>
        )
    }
    return (
        <Stack spacing={1}>
            <Grid>
                <Grid
                    container
                    size={12}
                    sx={{
                        borderTop: '1px solid black',
                        backgroundColor: 'white',
                        fontWeight: 'bold',
                        textAlign: 'center',
                    }}
                >
                    <Grid size={1.5} padding={1} sx={{ ...cellStyle }}>
                        {i18n.equipmentsNum}
                    </Grid>
                    <Grid size={1.5} padding={1} sx={{ ...cellStyle }}>
                        {i18n.samplingPointNum}
                    </Grid>
                    <Grid size={2} padding={1} sx={{ ...cellStyle, borderRight: '1px solid black' }}>
                        {i18n.pointLocation}
                    </Grid>
                    <Grid container size={7} sx={{ borderRight: '1px solid black' }}>
                        <Grid size={12/7*3} padding={1} sx={{ borderBottom: '1px solid black' }}>
                            {i18n.consumptionReport}
                        </Grid>
                        <Grid size={12/7*4} padding={1} sx={{ ...cellStyle }}>
                            {i18n.estimated}
                        </Grid>
                        <Grid size={12/7} padding={1} sx={{ borderBottom: '1px solid black' }}>
                            {i18n.estimatedVolume}
                        </Grid>
                        <Grid size={12/7} padding={1} sx={{ ...cellStyle }}>
                            {i18n.yearly}
                        </Grid>
                        <Grid size={12/7} padding={1} sx={{ ...cellStyle }}>
                            {i18n.lowWater}
                        </Grid>
                        <Grid size={12/7} padding={1} sx={{ ...cellStyle }}>
                            {i18n.pointStatus}
                        </Grid>
                        <Grid size={12/7} padding={1} sx={{ ...cellStyle }}>
                            {i18n.usesNumber}
                        </Grid>
                        <Grid size={12/7} padding={1} sx={{ ...cellStyle }}>
                            {i18n.yearVolume}
                        </Grid>
                        <Grid size={12/7} padding={1} sx={{ ...cellStyle }}>
                            {i18n.volumeLowWater}
                        </Grid>
                    </Grid>
                </Grid>
                {declaration.link_declarationInstallation.map((dI) => {
                    const inst = installationsWithGeo.find((i) => i.id === dI.idInstallation) || {}
                    const volumesConso = surveyVolumesExploitation.find((v) => v.idInstallation === dI.idInstallation) || {}
                    const volumes = volumesPoints.find((v) => v.id === dI.idInstallation) || {}
                    const materiels = getMateriels(dI.idInstallation)
                    const linkPoint = exploitation.link_samplings.find((l) => l.idInstallation === dI.idInstallation) || {}
                    const link_shared = linkPoint.link_shared || []
                    const pointIsShared = link_shared.filter((p) => p.idExploitation !== exploitation.idExploitation).length > 0
                    const total = link_shared.reduce((acc, l) => acc + (l.distribution || 0), 0)
                    const tooltip = (
                        <>
                            {link_shared.map((p) => getLineRepartition(p))}
                            <Typography sx={{ fontWeight: 'bold' }}>{i18n.total} : {`${total} %`}</Typography>
                        </>
                    )
                    const estimConso = getEstimConso(materiels.map((m) => m.id), linkPoint)
                    return (
                        <Grid key={dI.idInstallation} container sx={{ backgroundColor: 'white' }} >
                            <Grid
                                container
                                size={1.5}
                                padding={1}
                                spacing={1}
                                sx={{ ...cellStyle }}
                            >
                                {materiels.map((m, i) => {
                                    const shared = hasValue(m.administrator) && (m.administrator !== exploitation.operatorCode) && m.isShared
                                    return (
                                        <Grid key={m.id} container spacing={0.5} alignItems='center'>
                                            {m.reference}
                                            {shared && (
                                                <Tooltip title={`${i18n.shared} (${i18n.administrator} : ${contributorsIndex[m.administrator]?.name})`}>
                                                    <People />
                                                </Tooltip>
                                            )}
                                            {i !== (materiels.length - 1) && ', '}
                                        </Grid>
                                    )
                                })}
                            </Grid>
                            <Grid
                                container
                                size={1.5}
                                padding={1}
                                spacing={0.5}
                                alignItems='center'
                                sx={{ ...cellStyle }}
                            >
                                {inst.code}
                                {pointIsShared && (
                                    <Tooltip title={tooltip}>
                                        <People />
                                    </Tooltip>
                                )}
                            </Grid>
                            <Grid
                                size={2}
                                padding={1}
                                sx={{ ...cellStyle, borderRight: '1px solid black' }}
                            >
                                {inst.location}
                            </Grid>
                            <Grid size={1} padding={1} sx={{ borderBottom: '1px solid black' }}>
                                {formatMilliers(estimConso)}
                            </Grid>
                            <Grid size={1} padding={1} sx={{ ...cellStyle }}>
                                {formatMilliers(volumesConso?.total)}
                            </Grid>
                            <Grid size={1} padding={1} sx={{ ...cellStyle }}>
                                {formatMilliers(volumesConso?.lowWater)}
                            </Grid>
                            <Grid size={1} padding={1} sx={{ ...cellStyle }}>
                                {getStatusLabel(dI.stateCode)}
                            </Grid>
                            <Grid size={1} padding={1} sx={{ ...cellStyle }}>
                                {volumes.nbUsages}
                            </Grid>
                            <Grid size={1} padding={1} sx={{ ...cellStyle }}>
                                {formatMilliers(volumes.volumesAsked)}
                            </Grid>
                            <Grid
                                size={1}
                                padding={1}
                                sx={{ ...cellStyle, borderRight: '1px solid black' }}
                            >
                                {formatMilliers(volumes.lowWaterVolumeAsked)}
                            </Grid>
                        </Grid>
                    )
                })}
            </Grid>
            {(getSetting(survey.link_params, 'step3') || '1') !== '0' && (
                <Grid container justifyContent='center'>
                    <Button
                        variant='contained'
                        color='primary'
                        onClick={() => dispatch(push(`/survey/${survey.idSurvey}/declaration/${params.idDeclaration}/stepper/3`))}
                        disabled={readMode}
                    >
                        {i18n.change}
                    </Button>
                </Grid>
            )}
            <Grid container justifyContent='center'>
                {template(i18n.clickToRegisterDeclaration)({ date })}
            </Grid>
            <TextField
                id='comment'
                fullWidth
                label={i18n.comment}
                multiline
                rows={10}
                defaultValue={comment}
                disabled={readMode}
                onChange={(e) => setComment(e.target.value)}
            />
            {openModalNothingDeclared && (
                <ModalNothingDeclared
                    open={openModalNothingDeclared}
                    toggleModal={() => setOpenModalNothingDeclared(false)}
                    push={() => dispatch(push(`/survey/${survey.idSurvey}/declaration/${params.idDeclaration}/stepper/3`))}
                />
            )}
        </Stack>
    )
}

DeclarationRecapStep.propTypes = {
    readMode: PropTypes.bool,
    comment: PropTypes.string,
    setComment: PropTypes.func,
}

export default DeclarationRecapStep
